import React, { useEffect, useState } from 'react';
import { ToolFormat } from '..';
import styled from 'styled-components';
import CustomSwitch from '@src/components/CustomSwitch/CustomSwitch';
import { SettingsIcon2, SiteIcon, StatusAlertIcon } from '@src/components/Icons';
import CustomTooltip from '@src/components/CustomTootip/CustomTooltip';
import { toCapitalize } from '@src/helpers/Helpers';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	margin: 10px;
	padding-top: 12px;
	padding-bottom: 14px;
	gap: 10px;
	border-bottom: 1.5px solid #f1f1f1;
	@media screen and (max-width: 600px) {
		margin: 0;
	}
	#top {
		display: flex;
		align-items: center;
		justify-content: space-between;
		letter-spacing: 0.2px;
		#tool-details-conatainer {
			display: flex;
			align-items: center;
			gap: 14px;
			#image-container {
				width: 50px;
				height: 50px;
				border-radius: 8px;
				border: 1.5px solid #e9e9e9;
				position: relative;
				display: flex;
				align-items: center;
				justify-content: center;
				img {
					object-fit: contain;
				}
				#staus-alert-icon {
					position: absolute;
					left: 35px;
					top: 35px;
				}
			}

			#info {
				display: flex;
				flex-direction: column;
				line-height: 1.5;
				font-size: 16px;
				color: var(--lighter-text-color);
				#main-title {
					display: flex;
					align-items: center;
					gap: 8px;
					h3 {
						color: var(--default-text-color);
						font-weight: 700;
					}
					@media screen and (max-width: 600px) {
						flex-direction: column;
						align-items: flex-start;
					}
				}
				@media screen and (max-width: 600px) {
					font-size: 13px;
					#desc {
						max-width: 250px;
						overflow: hidden;
						white-space: normal;
					}
				}
			}
		}
		#tool-interactive-conatainer {
			display: flex;
			align-items: center;
			gap: 16px;
		}
	}
	#bottom {
		display: flex;
		flex-direction: column;
		gap: 8px;
		padding-left: 65px;
		p {
			color: var(--Gray-g-500, #979797);
			font-size: 14px;
			font-weight: 600;
			line-height: 19.1px;
			letter-spacing: 0.28px;
			@media screen and (max-width: 600px) {
				font-size: 12px;
			}
		}
		#tool-interactive-conatainer {
			display: flex;
			align-items: center;
			gap: 16px;
		}
	}
`;

interface StyledAnkerTagProps {
	enable?: boolean;
}

const StyledAnkerTag = styled.a<StyledAnkerTagProps>`
	display: flex;
	align-items: center;
	gap: 6px;
	color: ${(props) => (props.enable ? 'var(--default-text-color)' : 'var(--color-font-grey)')};
	font-size: 16px;
	font-weight: 700;
	cursor: pointer;
`;

const StatusLabel = styled.div`
	font-size: 14px;
	font-weight: 700;
	letter-spacing: 0.28px;
	line-height: 19.1px;
	margin-left: 8px;
	@media screen and (max-width: 600px) {
		font-size: 13px;
		margin-left: 0;
	}
`;

const statusColor: { [key: string]: string } = {
	Pending: '#E6AE06',
	Offline: '#ACACAC',
	Connected: '#00C853',
};

interface Props {
	tool: ToolFormat;
	setTools: React.Dispatch<React.SetStateAction<ToolFormat[]>>;
	setFilteredTools: React.Dispatch<React.SetStateAction<ToolFormat[]>>;
}

const ToolCard: React.FC<Props> = ({ tool, setTools, setFilteredTools }) => {
	//Mobile Screen States
	const [windowDimensions, setWindowDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	const updateWindowDimensions = () => {
		setWindowDimensions({
			width: window.innerWidth,
			height: window.innerHeight,
		});
	};

	//For Mobile Screen
	useEffect(() => {
		window.addEventListener('resize', updateWindowDimensions);

		return () => {
			window.removeEventListener('resize', updateWindowDimensions);
		};
	}, []);

	useEffect(() => {
		updateWindowDimensions();
	}, []);
	return (
		<StyledContainer>
			<div id="top">
				<div id="tool-details-conatainer">
					<div id="image-container">
						<img
							src={tool?.image?.url || '/placeholderImage.svg'}
							width={tool?.image?.width}
							height={tool?.image?.height}
							alt="tool-img"
						/>
						{tool?.status ? (
							<div id="staus-alert-icon">
								<StatusAlertIcon fill={statusColor[toCapitalize(tool?.status)]} />
							</div>
						) : null}
					</div>
					<div id="info">
						<div id="main-title">
							<h3>{tool?.name}</h3>
							<a
								href={tool?.site?.href}
								target="_blank"
								rel="noopener noreferrer"
								style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
							>
								<p>{tool?.site?.text}</p>
								<SiteIcon />
							</a>
							{windowDimensions?.width > 1150 && tool?.status ? (
								<StatusLabel style={{ color: statusColor[toCapitalize(tool?.status)] }}>
									{toCapitalize(tool?.status)}
								</StatusLabel>
							) : null}
						</div>
						<p id="desc">{tool?.description}</p>
					</div>
				</div>
				{windowDimensions?.width > 1150 ? (
					<div id="tool-interactive-conatainer">
						<StyledAnkerTag enable={tool?.enable} onClick={() => tool?.onClick(tool)}>
							<SettingsIcon2
								stroke={tool?.enable ? 'var(--default-text-color)' : 'var(--color-font-grey)'}
							/>
							Manage
						</StyledAnkerTag>
						{tool?.switcherDisable ? (
							<CustomTooltip
								title={`${tool?.name} integration has not been completed yet. Please navigate to the 'Manage' section.`}
								placement="top"
							>
								<span>
									<CustomSwitch
										title=""
										color="customGreen"
										checked={false}
										onToggle={() => {}}
										disabled={tool?.switcherDisable}
									/>
								</span>
							</CustomTooltip>
						) : (
							<CustomSwitch
								title=""
								color="customGreen"
								checked={tool?.enable ? tool?.enable : false}
								onToggle={() => tool?.handleSwitchToggle(tool)}
								disabled={tool?.loading}
							/>
						)}
					</div>
				) : null}
			</div>
			<div id="bottom">
				{tool?.last_activity ? <p>{`last activity: ${tool?.last_activity}`}</p> : null}
				{windowDimensions?.width <= 1150 ? (
					<div id="tool-interactive-conatainer">
						<StyledAnkerTag enable={tool?.enable} onClick={() => tool?.onClick(tool)}>
							<SettingsIcon2
								stroke={tool?.enable ? 'var(--default-text-color)' : 'var(--color-font-grey)'}
							/>
							Manage
						</StyledAnkerTag>
						{tool?.switcherDisable ? (
							<CustomTooltip
								title={`${tool?.name} integration has not been completed yet. Please navigate to the 'Manage' section.`}
								placement="top"
							>
								<span>
									<CustomSwitch
										title=""
										color="customGreen"
										checked={false}
										onToggle={() => {}}
										disabled={tool?.switcherDisable}
									/>
								</span>
							</CustomTooltip>
						) : (
							<CustomSwitch
								title=""
								color="customGreen"
								checked={tool?.enable ? tool?.enable : false}
								onToggle={() => tool?.handleSwitchToggle(tool)}
								disabled={tool?.loading}
							/>
						)}
						{windowDimensions?.width <= 1150 && tool?.status ? (
							<StatusLabel style={{ color: statusColor[toCapitalize(tool?.status)] }}>
								{toCapitalize(tool?.status)}
							</StatusLabel>
						) : null}
					</div>
				) : null}
			</div>
		</StyledContainer>
	);
};

export default ToolCard;
